import { Alert, Skeleton, SkeletonGroup } from '@/components/ui';
import { useAnnounceOnChange } from '@/lib/a11y';
import type { ApiError } from '@/lib/api/client';
import type { ServiceabilityOut } from '@/lib/api/types';

import { formatEta } from './eta';

export interface PincodeLookupProps {
  serviceability: ServiceabilityOut | undefined;
  isLoading: boolean;
  error: ApiError | null;
  /** True once the field holds six digits — before that there is nothing to say. */
  enabled: boolean;
}

/**
 * The answer to "do you deliver here?", shown the moment the PIN code is
 * complete and before any other address field is asked for.
 *
 * That ordering is the whole point of the step: there is no reason to make
 * somebody type a house number, a landmark and a city before telling them we do
 * not deliver to their town. The server's `message` is customer-facing prose and
 * is rendered verbatim — it already says what to do about an unserviceable PIN.
 */
export function PincodeLookup({ serviceability, isLoading, error, enabled }: PincodeLookupProps) {
  const eta = serviceability
    ? formatEta(serviceability.eta_days_min, serviceability.eta_days_max)
    : null;

  // "We don't deliver there" is blocking news and interrupts; a successful
  // lookup waits its turn.
  useAnnounceOnChange(
    serviceability ? serviceability.message : null,
    serviceability && !serviceability.is_serviceable ? 'assertive' : 'polite',
  );

  if (!enabled) return null;

  if (isLoading) {
    return (
      <SkeletonGroup label="Checking whether we deliver there">
        <Skeleton className="h-12 w-full" />
      </SkeletonGroup>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" title="We could not check that PIN code" live>
        <p>{error.message}</p>
        <p>You can still fill in the address — we will confirm delivery before you pay.</p>
      </Alert>
    );
  }

  if (!serviceability) return null;

  return serviceability.is_serviceable ? (
    <Alert severity="success" title="We deliver here">
      <p>{serviceability.message}</p>
      {/* "today after printing" does not read, and the same-day case is the
          one worth saying clearly rather than squeezing into the range
          sentence. */}
      {eta === 'today' ? (
        <p className="text-ink-subtle">Printed and delivered the same day.</p>
      ) : eta ? (
        <p className="text-ink-subtle">Estimated delivery: {eta} after printing.</p>
      ) : null}
    </Alert>
  ) : (
    <Alert severity="danger" title="We do not deliver here yet" live>
      <p>{serviceability.message}</p>
    </Alert>
  );
}
